import { Input } from "@/components/ui/input";

export interface AllocationRow {
  invoiceId: number;
  invoiceRef: string;
  /** Outstanding balance on the invoice (read-only display). */
  balance: number;
  /** Amount to allocate from this payment to this invoice. */
  allocate: number;
}

export interface PaymentAllocationGridProps {
  rows: AllocationRow[];
  /** Called when the user changes the allocate amount for a row. */
  onAllocateChange: (invoiceId: number, amount: number) => void;
  /** Total payment amount — displayed alongside the running allocation total. */
  paymentTotal?: number;
}

function fmt(n: number) {
  return n.toLocaleString("en-AU", { style: "currency", currency: "AUD" });
}

export function PaymentAllocationGrid({
  rows,
  onAllocateChange,
  paymentTotal,
}: PaymentAllocationGridProps) {
  const runningTotal = rows.reduce((sum, r) => sum + (Number.isFinite(r.allocate) ? r.allocate : 0), 0);

  return (
    <div className="space-y-2">
      <div className="overflow-x-auto rounded-xl border bg-white shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="px-4 py-3 text-left text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Invoice</th>
              <th className="px-4 py-3 text-right text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">Balance</th>
              <th className="px-4 py-3 text-right text-[10px] uppercase tracking-wider text-muted-foreground font-semibold w-40">Allocate</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.length === 0 ? (
              <tr>
                <td colSpan={3} className="p-8 text-center text-muted-foreground text-sm">
                  No invoices to allocate
                </td>
              </tr>
            ) : (
              rows.map((row) => (
                <tr key={row.invoiceId} className="group hover:bg-slate-50/50 transition-colors">
                  <td className="px-4 py-3 font-medium text-slate-900">{row.invoiceRef}</td>
                  <td className="px-4 py-3 text-right text-slate-600">{fmt(row.balance)}</td>
                  <td className="px-4 py-2 text-right">
                    <div className="relative inline-flex items-center">
                      <span className="absolute left-3 text-muted-foreground text-sm pointer-events-none">$</span>
                      <Input
                        type="number"
                        min={0}
                        step={0.01}
                        aria-label={`Allocate to ${row.invoiceRef}`}
                        value={row.allocate}
                        onChange={(e) =>
                          onAllocateChange(row.invoiceId, Number(e.target.value))
                        }
                        className="w-32 text-right pl-7"
                      />
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
          <tfoot className="border-t bg-slate-50">
            <tr>
              <td className="px-4 py-3 font-semibold text-slate-900" colSpan={2}>
                Total allocated
                {paymentTotal !== undefined && (
                  <span className="ml-2 text-muted-foreground font-normal text-xs uppercase tracking-wider">
                    (of {fmt(paymentTotal)})
                  </span>
                )}
              </td>
              <td
                className={`px-4 py-3 text-right font-bold ${
                  paymentTotal !== undefined && runningTotal > paymentTotal
                    ? "text-destructive"
                    : "text-slate-900"
                }`}
                data-testid="allocation-total"
              >
                {fmt(runningTotal)}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}
